import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Copy, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/lib/i18n";
import { api, errorMessage } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { AdminLine } from "@/components/admin/AdminLine";

const STATUSES = ["awaiting_payment", "paid", "sent_to_ton_atelier", "in_production", "shipped", "delivered", "cancelled"];

const Money = ({ label, value, accent, testid }) => (
  <div className="flex justify-between py-1.5 text-sm"><span className="text-slate-400">{label}</span><span className={`font-mono-num font-bold ${accent || ""}`} data-testid={testid}>{value}</span></div>
);

export default function AdminOrderDetail() {
  const { id } = useParams();
  const { t, money, lang } = useLang();
  const [o, setO] = useState(null);
  const [form, setForm] = useState({ status: "", tracking_number: "", admin_notes: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/admin/orders/${id}`).then((r) => {
      setO(r.data);
      setForm({ status: r.data.status, tracking_number: r.data.tracking_number || "", admin_notes: r.data.admin_notes || "" });
    });
  }, [id]);

  if (!o) return <p className="text-slate-500">{t("common.loading")}</p>;
  const s = o.shipping;
  const address = [o.customer.business || o.customer.name, o.customer.business ? `Attn: ${o.customer.name}` : null, s.line1, s.line2, `${s.city} ${s.province} ${s.postal_code}`, "Canada", o.customer.phone].filter(Boolean).join("\n");
  const fmtDate = (d) => new Date(d).toLocaleString(lang === "fr" ? "fr-CA" : "en-CA", { dateStyle: "medium", timeStyle: "short" });

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/admin/orders/${id}`, form);
      setO(data);
      toast.success(t("admin.updated"));
    } catch (e) {
      toast.error(errorMessage(e, t));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-order-detail">
      <Link to="/admin" className="inline-flex items-center gap-2 text-sm font-bold text-slate-400 hover:text-white" data-testid="admin-back-btn"><ArrowLeft className="h-4 w-4" /> {t("admin.back")}</Link>
      <div className="mt-4 flex flex-wrap items-center gap-4">
        <h1 className="font-mono-num text-3xl font-bold text-gold" data-testid="admin-order-number">{o.order_number}</h1>
        <StatusBadge status={o.status} testid="admin-order-status" />
        <span className="text-sm text-slate-500">{fmtDate(o.created_at)} · {t("admin.lang")}: {o.language.toUpperCase()} · <span data-testid="admin-payment-provider">{(o.payment_provider || "stripe") === "paypal" ? "PayPal" : "Stripe"}</span></span>
      </div>
      <div className="mt-8 grid gap-6 xl:grid-cols-[1fr_340px]">
        <div className="flex flex-col gap-6">
          <div className="grid gap-6 md:grid-cols-2">
            <section className="panel p-5" data-testid="admin-customer">
              <h2 className="label">{t("admin.contact")}</h2>
              <p className="font-bold">{o.customer.name}</p>
              {o.customer.business && <p className="text-slate-300">{o.customer.business}</p>}
              <a href={`mailto:${o.customer.email}`} className="block text-sm text-gold hover:underline">{o.customer.email}</a>
              <p className="text-sm text-slate-300">{o.customer.phone}</p>
            </section>
            <section className="panel p-5" data-testid="admin-ship-to">
              <div className="flex items-start justify-between"><h2 className="label">{t("admin.ship_to")}</h2>
                <button onClick={() => { navigator.clipboard.writeText(address); toast.success(t("admin.copied")); }} className="text-slate-400 hover:text-gold" data-testid="admin-copy-address" aria-label={t("admin.copy")}><Copy className="h-4 w-4" /></button>
              </div>
              <pre className="whitespace-pre-wrap font-sans text-sm text-slate-200">{address}</pre>
            </section>
          </div>
          <section className="panel px-5 pt-5"><h2 className="label">{t("admin.lines")}</h2>
            {o.items.map((l, i) => <AdminLine key={i} line={l} index={i} orderNumber={o.order_number} />)}
          </section>
        </div>
        <div className="flex flex-col gap-6">
          <section className="panel p-5" data-testid="admin-breakdown">
            <h2 className="label">{t("admin.breakdown")}</h2>
            <Money label={t("admin.ta_cost")} value={money(o.cost_total)} testid="admin-cost-total" />
            <Money label={t("admin.your_profit")} value={money(o.profit_total)} accent="text-emerald-400" testid="admin-profit-total" />
            <Money label={t("admin.subtotal")} value={money(o.subtotal)} />
            <Money label={t("admin.shipping")} value={money(o.shipping_cost)} />
            <Money label={t("admin.tax")} value={money(o.tax)} />
            <div className="mt-2 border-t border-white/10 pt-2"><Money label={t("admin.paid")} value={o.amount_paid != null ? money(o.amount_paid) : "—"} accent="text-gold" testid="admin-amount-paid" /></div>
          </section>
          <section className="panel flex flex-col gap-4 p-5" data-testid="admin-manage">
            <h2 className="label">{t("admin.manage")}</h2>
            <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="field" data-testid="admin-status-select">
              {STATUSES.map((st) => <option key={st} value={st} className="bg-navy">{t(`admin_status.${st}`)}</option>)}
            </select>
            <label><span className="label">{t("admin.tracking")}</span><input value={form.tracking_number} onChange={(e) => setForm({ ...form, tracking_number: e.target.value })} className="field font-mono-num" data-testid="admin-tracking-input" /></label>
            <label><span className="label">{t("admin.notes")}</span><textarea rows={3} value={form.admin_notes} onChange={(e) => setForm({ ...form, admin_notes: e.target.value })} className="field" data-testid="admin-notes-input" /></label>
            <button onClick={save} disabled={busy} className="btn-gold" data-testid="admin-save-order-btn">{busy && <Loader2 className="h-4 w-4 animate-spin" />} {t("admin.update")}</button>
          </section>
          <section className="panel p-5" data-testid="admin-history">
            <h2 className="label">{t("admin.history")}</h2>
            <ol className="flex flex-col gap-2">
              {[...o.history].reverse().map((h, i) => <li key={i} className="flex items-center justify-between gap-2 text-sm"><StatusBadge status={h.status} /><span className="text-xs text-slate-500">{fmtDate(h.at)}</span></li>)}
            </ol>
            {o.emails?.length > 0 && (
              <ul className="mt-4 flex flex-col gap-1 border-t border-white/10 pt-3 text-xs" data-testid="admin-emails">
                {o.emails.map((m, i) => (
                  <li key={i} className={`flex justify-between ${m.sent ? "text-slate-400" : "text-ruby"}`}>
                    <span>{t(`admin.email_${m.type}`)} {m.sent ? "✓" : "✗"}</span><span>{fmtDate(m.at)}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
